import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule, NgStyle, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { L10nLocale, L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import {
  catchError,
  debounceTime,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { GroupsApi, GroupTreeNode, PagedGroups, PagedGroupTreeNodeResponse } from 'src/app/hickory-api';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { LoadingComponent } from '../loading/loading.component';
import { areArraysEqual, deepCopy } from '../model/utility';

interface GroupNode {
  group: GroupTreeNode;
  indentLevel: number;
  expanded?: boolean;
  loading?: boolean;
}

type OptionalClassification = 'All' | 'Public' | 'Internal';

@Component({
  selector: 'app-user-groups',
  templateUrl: './user-groups.component.html',
  styleUrls: ['./user-groups.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgStyle,
    NgTemplateOutlet,
    ReactiveFormsModule,
    L10nTranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatChipsModule,
    LoadingComponent,
  ],
})
export class UserGroupsComponent implements OnInit {
  readonly initialClassification = input<OptionalClassification>('All');
  readonly existingGroups = input<GroupTreeNode[]>([]);
  readonly existingGroupIds = input<number[]>([]);
  readonly disabled = input(false);
  readonly topLevelSelection = input(false);
  readonly topLevelDisablesChildren = input(false);
  readonly limitGroups = input<number[] | null>(null);
  readonly deselectedLimitedGroupsOnChange = input(false);
  readonly showSelectedGroups = input(true);
  readonly defaultTenantId = input<number | undefined>(undefined);

  readonly groupsChange = output<GroupTreeNode[]>();

  readonly topLevelGroups = signal<{ [key: string]: GroupNode[] }>({
    Public: [],
    Internal: [],
    All: [],
  });

  readonly groups = signal(new Map<number, GroupTreeNode>());
  readonly parentToChildrenMap = signal(new Map<number, GroupNode[]>());
  readonly linkedChildToParentsMap = signal(new Map<number, GroupNode[]>());

  readonly selectedGroupNodes = signal<GroupNode[]>([]);

  readonly classification = new FormControl<OptionalClassification>('All');
  readonly searchControl = new FormControl<string>('');
  readonly tenantId = signal<number | undefined>(undefined);

  readonly isLoading = signal(false);
  readonly hasLoadError = signal(false);

  private previousRx$: Observable<unknown> | undefined;

  private readonly maxParallelLoads = 2;
  private readonly groupLoadQueue$ = new Subject<number>();

  private readonly groupsApi = inject(GroupsApi);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  readonly locale = inject<L10nLocale>(L10N_LOCALE);

  readonly canAssignToParentGroups = computed(() => {
    const hasPermission = this.authService.hasPermission('hickory.parent-groups-membership');
    const topLevelSelection = this.topLevelSelection();
    return hasPermission || topLevelSelection;
  });

  readonly selectedGroups = computed(() => {
    const selectedNodes = this.selectedGroupNodes();
    return selectedNodes.map((group) => group.group);
  });

  private previousLimitGroups: number[] | null = null;

  constructor() {
    effect(
      () => {
        const limitGroups = this.limitGroups();
        const previousLimit = this.previousLimitGroups;
        const hasChanged = !areArraysEqual(limitGroups, previousLimit);

        this.previousLimitGroups = limitGroups ? [...limitGroups] : null;

        if (hasChanged && limitGroups !== null) {
          untracked(() => this.handleLimitGroupsChange(limitGroups, previousLimit));
        }
      },
      { allowSignalWrites: true },
    );
  }

  private handleLimitGroupsChange(limitGroups: number[], previousLimit: number[] | null): void {
    this.tenantId.set(this.defaultTenantId() ?? this.authService.user.snapshot?.tenantId);
    this.onSearch(undefined, this.classification.value);

    if (this.deselectedLimitedGroupsOnChange()) {
      this.deselectRemovedGroups(limitGroups, previousLimit);
      this.filterGroupsMap(limitGroups);
    }
  }

  private deselectRemovedGroups(limitGroups: number[], previousLimit: number[] | null): void {
    const removedFromLimit = (previousLimit ?? []).filter((id) => !limitGroups.includes(id));

    if (removedFromLimit.length === 0) {
      return;
    }

    const groupsToDeselect = this.selectedGroupNodes().filter(
      (node) => removedFromLimit.includes(node.group.id!) || this.isAncestorInList(node.group, removedFromLimit),
    );

    groupsToDeselect.forEach((node) => this.onToggleGroup(node, false));
  }

  private filterGroupsMap(limitGroups: number[]): void {
    const filtered = new Map<number, GroupTreeNode>(
      Array.from(this.groups().entries()).filter(([id]) => limitGroups.includes(id)),
    );
    this.groups.set(filtered);
  }

  ngOnInit(): void {
    this.classification.setValue(this.initialClassification());
    this.tenantId.set(this.defaultTenantId() ?? this.authService.user.snapshot?.tenantId);

    this.checkExistingGroups();
    this.subscribeToSearch();
    this.subscribeToClassification();
    this.subscribeToGroupLoadQueue();
    this.getParentGroups(this.classification.value, true);
  }

  checkExistingGroups(): void {
    const existingGroups = this.existingGroups();
    for (const group of existingGroups) {
      this.selectedGroupNodes.update((nodes) => [...nodes, { group: group, indentLevel: 0 }]);
    }

    const existingGroupIds = this.existingGroupIds();
    if (!existingGroupIds?.length) {
      this.groupsChange.emit([]);
      return;
    }

    this.groupsApi
      .getGroupsV4(this.tenantId()!, existingGroupIds, undefined, 1, existingGroupIds.length)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((response) => {
          if (response.items) {
            this.selectedGroupNodes.set(
              response.items.map((group) => {
                return { group, indentLevel: 0 };
              }),
            );

            this.populateGroups(response.items, null);
            this.recurseUpGroupTree(response.items);
            this.groupsChange.emit(this.selectedGroups());
          }
        }),
      )
      .subscribe();
  }

  getGroupById(groupId: number): Observable<GroupTreeNode | undefined> {
    return this.groupsApi.getGroupsV4(this.tenantId()!, [groupId], undefined, 1, 1).pipe(
      catchError((error) => {
        this.hasLoadError.set(true);
        return throwError(() => error);
      }),
      map((response) => (response.items ? response.items[0] : undefined)),
    );
  }

  private readonly currentlyLoadingGroups = new Set<number>();
  private getParentGroupTree(groupId: number) {
    this.currentlyLoadingGroups.add(groupId);
    this.groupLoadQueue$.next(groupId);
  }

  private loadGroupTree(groupId: number) {
    return this.getGroupById(groupId).pipe(
      switchMap((group: GroupTreeNode | undefined) => {
        if (!group) {
          return of([undefined, undefined]);
        }
        const currentGroups = this.groups();
        if (group.parentId && !currentGroups.has(group.parentId)) {
          return forkJoin([of(group), this.getGroupById(group.parentId)]);
        }

        if (!group.parentId) {
          return of([group, undefined]);
        }

        return of([group, currentGroups.get(group.parentId)]);
      }),
      tap(([group, parentGroup]: (GroupTreeNode | undefined)[]) => {
        if (!group) {
          return;
        }
        this.hasLoadError.set(false);
        this.currentlyLoadingGroups.delete(groupId);
        this.populateGroups([group], parentGroup ?? null);
        this.recurseUpGroupTree([group]);
      }),
      finalize(() => this.currentlyLoadingGroups.delete(groupId)),
    );
  }

  private recurseUpGroupTree(groups: GroupTreeNode[]) {
    const parentIds = new Set<number>();
    for (const group of groups) {
      if (group.parentId) {
        parentIds.add(group.parentId);
      }
    }

    for (const id of parentIds) {
      this.getParentGroupTree(id);
    }
  }

  subscribeToSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.searchRx?.unsubscribe()),
        debounceTime(300),
        tap((value: string | null | undefined) => {
          const limitGroups = this.limitGroups();
          if (!value && !limitGroups?.length) {
            this.getParentGroups(this.classification.value, true);
          } else {
            this.onSearch(value, this.classification.value);
          }
        }),
      )
      .subscribe();
  }

  subscribeToGroupLoadQueue() {
    this.groupLoadQueue$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap((groupId: number) => this.loadGroupTree(groupId), this.maxParallelLoads),
      )
      .subscribe();
  }

  subscribeToClassification(): void {
    this.classification.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.classification.value),
        pairwise(),
        tap(([prev, curr]) => {
          if (this.searchControl.value) {
            this.searchControl.setValue('', { emitEvent: false });
            this.topLevelGroups.update((groups) => ({ ...groups, [prev ?? 'All']: [] }));
          }

          this.onClassificationChange(curr ?? 'All');
        }),
      )
      .subscribe();
  }

  onClassificationChange(classification: OptionalClassification): void {
    const limitGroups = this.limitGroups();
    if (limitGroups?.length) {
      this.onSearch(undefined, classification);
    } else {
      this.getParentGroups(classification === 'All' ? null : classification);
    }
  }

  retryPreviousRequest(): void {
    if (this.previousRx$) {
      this.isLoading.set(true);
      this.previousRx$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  searchRx: Subscription | undefined;
  onSearch(
    value: string | null | undefined,
    classification: OptionalClassification | null = this.classification.value,
  ): void {
    const limitGroups = this.limitGroups();
    // if limit groups is empty, don't make the call
    if (limitGroups && !limitGroups.length) {
      this.topLevelGroups.update((groups) => ({ ...groups, [classification || 'All']: [] }));
      return;
    }

    if (classification === 'All') {
      classification = null;
    }

    if (this.searchRx) {
      this.searchRx.unsubscribe();
    }

    this.isLoading.set(true);
    this.hasLoadError.set(false);
    this.previousRx$ = this.groupsApi
      .getGroupsV4(
        this.tenantId()!,
        limitGroups?.length ? limitGroups : undefined,
        undefined,
        1,
        500,
        value ?? undefined,
        classification as 'Public' | 'Internal' | undefined,
      )
      .pipe(
        catchError((error) => {
          this.hasLoadError.set(true);
          return throwError(() => error);
        }),
        tap((groups: PagedGroups) => {
          this.hasLoadError.set(false);
          const type = classification || 'All';

          this.populateGroups(groups.items ?? [], null);

          this.topLevelGroups.update((current) => ({
            ...current,
            [type]: groups.items?.map((group) => ({ group, indentLevel: 0 })) ?? [],
          }));
        }),
        finalize(() => this.isLoading.set(false)),
      );

    this.searchRx = this.previousRx$.subscribe();
  }

  populateGroups(groups: GroupTreeNode[], parentGroup: GroupTreeNode | null): void {
    if (parentGroup) {
      this.parentToChildrenMap.update((map) => {
        const newMap = new Map(map);
        newMap.set(
          parentGroup.id as number,
          groups.map((group) => ({ group, indentLevel: 1 })),
        );
        return newMap;
      });
    }

    this.groups.update((currentGroups) => {
      const newGroups = new Map(currentGroups);
      groups.forEach((group) => {
        if (!newGroups.has(group.id!)) {
          newGroups.set(group.id!, group);
        }
      });
      return newGroups;
    });

    const currentParentToChildrenMap = this.parentToChildrenMap();
    const currentLinkedChildToParentsMap = this.linkedChildToParentsMap();

    const parentToChildrenUpdates = new Map<number, GroupNode[]>();
    const linkedChildToParentsUpdates = new Map<number, GroupNode[]>();

    // eslint-disable-next-line sonarjs/cognitive-complexity
    groups.forEach((group) => {
      if (!parentGroup) {
        if (!group.parentId) {
          return;
        }

        if (currentParentToChildrenMap.has(group.parentId) || parentToChildrenUpdates.has(group.parentId)) {
          const children = parentToChildrenUpdates.get(group.parentId) ?? [
            ...(currentParentToChildrenMap.get(group.parentId) || []),
          ];
          if (!children?.some((c) => c.group.id === group.id)) {
            children.push({ group, indentLevel: 1 });
          }
          parentToChildrenUpdates.set(group.parentId, children);
        } else {
          parentToChildrenUpdates.set(group.parentId, [{ group, indentLevel: 1 }]);
        }
      }

      if (group.isLink && parentGroup) {
        if (currentLinkedChildToParentsMap.has(group.id!) || linkedChildToParentsUpdates.has(group.id!)) {
          const parents = linkedChildToParentsUpdates.get(group.id!) ?? [
            ...(currentLinkedChildToParentsMap.get(group.id!) || []),
          ];
          if (!parents?.some((p) => p.group.id === parentGroup.id)) {
            parents.push({ group: parentGroup, indentLevel: 0 });
          }
          linkedChildToParentsUpdates.set(group.id!, parents);
        } else {
          linkedChildToParentsUpdates.set(group.id!, [{ group: parentGroup, indentLevel: 0 }]);
        }
      }
    });

    if (parentToChildrenUpdates.size > 0) {
      this.parentToChildrenMap.update((map) => {
        const newMap = new Map(map);
        parentToChildrenUpdates.forEach((value, key) => newMap.set(key, value));
        return newMap;
      });
    }

    if (linkedChildToParentsUpdates.size > 0) {
      this.linkedChildToParentsMap.update((map) => {
        const newMap = new Map(map);
        linkedChildToParentsUpdates.forEach((value, key) => newMap.set(key, value));
        return newMap;
      });
    }
  }

  parentGroupRx: Subscription | undefined;
  getParentGroups(classification: OptionalClassification | null = this.classification.value, refresh = false): void {
    const limitGroups = this.limitGroups();
    if (limitGroups !== null) {
      this.topLevelGroups.update((groups) => ({ ...groups, [classification || 'All']: [] }));
      return;
    }

    const currentTopLevelGroups = this.topLevelGroups();
    if (!classification && currentTopLevelGroups['All'] && currentTopLevelGroups['All'].length > 0 && !refresh) {
      return;
    }

    if (
      !!classification &&
      currentTopLevelGroups[classification ?? 'All'] &&
      currentTopLevelGroups[classification ?? 'All']!.length > 0 &&
      !refresh
    ) {
      return;
    }

    if (classification === 'All') {
      classification = null;
    }

    if (this.parentGroupRx) {
      this.parentGroupRx.unsubscribe();
    }

    this.isLoading.set(true);
    this.hasLoadError.set(false);
    this.previousRx$ = this.groupsApi
      .getGroupTreeNodesV4(this.tenantId()!, undefined, classification as 'Public' | 'Internal' | undefined, 1, 500)
      .pipe(
        catchError((error) => {
          this.hasLoadError.set(true);
          return throwError(() => error);
        }),
        tap((groups: PagedGroupTreeNodeResponse) => {
          this.hasLoadError.set(false);
          const type = classification || 'All';

          this.populateGroups(groups.items ?? [], null);

          const currentGroups = this.groups();
          this.topLevelGroups.update((current) => ({
            ...current,
            [type]:
              groups.items
                ?.filter((g) => !g.parentId || !currentGroups.has(g.parentId))
                ?.map((group) => ({ group: group, indentLevel: 0 })) ?? [],
          }));
        }),
        finalize(() => this.isLoading.set(false)),
      );

    this.parentGroupRx = this.previousRx$.subscribe();
  }

  onExpandNode(node: GroupNode): void {
    if (node.expanded) {
      node.expanded = false;
      return;
    }

    const existingChildren = this.getChildren(node);
    const allChildrenProperlyLoaded =
      this.hasChildren(node.group) &&
      existingChildren?.length &&
      existingChildren?.every((child) => child.group?.childGroupCount && child.group?.childGroupCount >= 0);

    if (this.hasChildren(node.group) && !allChildrenProperlyLoaded) {
      node.loading = true;
      this.previousRx$ = this.groupsApi
        .getGroupTreeNodesV4(
          this.tenantId()!,
          node.group.id!,
          this.classification.value === 'All' ? undefined : this.classification.value!,
          1,
          500,
        )
        .pipe(
          catchError((error) => {
            this.hasLoadError.set(true);
            return throwError(() => error);
          }),
          tap((children: PagedGroupTreeNodeResponse) => {
            this.hasLoadError.set(false);
            this.populateGroups(children.items ?? [], node.group);
            node.expanded = true;
          }),
          finalize(() => (node.loading = false)),
        );

      this.previousRx$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    } else {
      node.expanded = true;
    }
  }

  onToggleGroup(node: GroupNode, checked: boolean | undefined): void {
    checked = checked ?? !this.isGroupSelected(node.group);

    if (checked) {
      this.onAddGroup(node);
    } else {
      this.onRemoveGroup(node);
    }

    this.groupsChange.emit(this.selectedGroups());
  }

  private onAddGroup(node: GroupNode): void {
    if (this.isGroupSelected(node.group)) {
      return;
    }

    this.selectedGroupNodes.update((nodes) => [...nodes, deepCopy(node)]);
  }

  private onRemoveGroup(node: GroupNode): void {
    this.selectedGroupNodes.update((nodes) => nodes.filter((group) => group.group.id !== node.group.id));

    this.removeDescendingChildren(node);
  }

  private removeDescendingChildren(node: GroupNode): void {
    const children = this.getChildren(node);
    children.forEach((child) => {
      this.selectedGroupNodes.update((nodes) => nodes.filter((group) => group.group.id !== child.group.id));
      this.removeDescendingChildren(child);
    });
  }

  /**
   * Checks if any ancestor of the given group is in the provided list of IDs.
   * Used to determine if a child group should be deselected when its parent is removed from the limit.
   */
  private isAncestorInList(group: GroupTreeNode, ancestorIds: number[]): boolean {
    if (!group?.parentId) {
      return false;
    }

    if (ancestorIds.includes(group.parentId)) {
      return true;
    }

    // Recursively check up the tree using loaded groups
    const parentGroup = this.groups().get(group.parentId);
    if (parentGroup) {
      return this.isAncestorInList(parentGroup, ancestorIds);
    }

    return false;
  }

  isNodeDisabled(node: GroupNode): boolean {
    let disabled = this.disabled();

    if (this.topLevelDisablesChildren()) {
      disabled = disabled || this.isParentGroupSelected(node.group);
    }

    return disabled || (this.hasChildren(node.group) && !this.canAssignToParentGroups());
  }

  isGroupSelected(group: GroupTreeNode): boolean {
    if (!group) {
      return false;
    }

    return this.selectedGroupNodes().some((node) => node.group.id === group.id);
  }

  isParentGroupSelected(group: GroupTreeNode | undefined): boolean {
    if (!group) {
      return false;
    }

    const linkedMap = this.linkedChildToParentsMap();
    const linked = linkedMap.get(group.id!);
    if (!group?.parentId && !linked?.length) {
      return false;
    }

    const selectedParent = this.selectedGroupNodes().find((node) => node.group.id === group.parentId);
    if (selectedParent) {
      return true;
    }

    const currentGroups = this.groups();
    const parentGroup: GroupTreeNode | undefined = currentGroups.get(group.parentId!);
    return (
      this.isParentGroupSelected(parentGroup) ||
      (linked?.some((node) => this.isGroupSelected(node.group) || this.isParentGroupSelected(node.group)) ?? false)
    );
  }

  isIndeterminate(node: GroupNode): boolean {
    if (this.isGroupSelected(node.group)) {
      return false;
    }

    const parentSelected = this.isParentGroupSelected(node.group);
    if (parentSelected) {
      return false;
    }

    const children = this.getChildren(node);
    if (this.hasChildren(node.group)) {
      return children.some((child) => this.isGroupSelected(child.group) || this.isIndeterminate(child));
    }

    return false;
  }

  getChildren(node: GroupNode | undefined): GroupNode[] {
    if (!node) {
      return [];
    }

    const parentToChildrenMap = this.parentToChildrenMap();
    if (parentToChildrenMap.has(node.group.id!)) {
      return parentToChildrenMap.get(node.group.id!) ?? [];
    }

    return [];
  }

  hasChildren(node: GroupTreeNode | undefined): boolean {
    if (!node) {
      return false;
    }

    const parentToChildrenMap = this.parentToChildrenMap();
    return (node.childGroupCount && node.childGroupCount > 0) || parentToChildrenMap.has(node.id!);
  }

  getIndentLevel(node: GroupNode): string {
    return node.indentLevel * 10 + 'px';
  }
}
